import type {
    ReactiveDropdownCatalogPhase,
    ReactiveFormSupportDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';

const DECLARATIVE_DROPDOWN_FORM_TYPE_LIMIT = 3;

export const PARCELAS_FORM_SUPPORT_DATA_KEYS = {
    patronVina: 'patronVina',
    sistemasRiego: 'sistemasRiego',
    comunidadesRegantes: 'comunidadesRegantes',
    origenesAguaRiego: 'origenesAguaRiego',
    fincas: 'fincas',
    sectores: 'sectores',
    compradores: 'compradores',
    aplicadores: 'aplicadores',
    usuarios: 'usuarios',
    legacyCultivos: 'legacyCultivos',
    legacyVariedades: 'legacyVariedades',
} as const;

export type ParcelasFormSupportDataKey = typeof PARCELAS_FORM_SUPPORT_DATA_KEYS[keyof typeof PARCELAS_FORM_SUPPORT_DATA_KEYS];

export interface ParcelasFormSupportDataSourceConfig {
    readonly formType: number;
    readonly env: ParcelasEnvironmentDefinition;
}

/** Shared load-plan rules for Parcelas support data. */
export class ParcelasFormLoadPlan {
    public static createSupportDataSources(
        config: ParcelasFormSupportDataSourceConfig,
    ): readonly ReactiveFormSupportDataSource<ParcelasModel>[] {
        const sources: ReactiveFormSupportDataSource<ParcelasModel>[] = [
            this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.patronVina, 'deferred'),
        ];

        if (!this.usesDeclarativeSimpleDropdowns(config.formType)) {
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.sistemasRiego, 'critical'));
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.comunidadesRegantes, 'critical'));
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.origenesAguaRiego, 'critical'));
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.aplicadores, 'critical'));
        }

        if (!this.usesDeclarativeFincaSectorDropdowns(config.formType)) {
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.fincas, 'critical'));
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.sectores, 'critical'));
        }

        if (config.env.showParcelasPartida) {
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.compradores, 'deferred'));
        }

        if (config.env.appName === 'avafitogest') {
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.usuarios, 'deferred'));
        }

        if (this.shouldLoadLegacyCultivoCatalogs(config.env)) {
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.legacyCultivos, 'critical'));
            sources.push(this.createSupportDataSource(PARCELAS_FORM_SUPPORT_DATA_KEYS.legacyVariedades, 'critical'));
        }

        return sources;
    }

    public static usesDeclarativeFincaSectorDropdowns(formType: number): boolean {
        return formType < DECLARATIVE_DROPDOWN_FORM_TYPE_LIMIT;
    }

    public static usesDeclarativeSimpleDropdowns(formType: number): boolean {
        return formType < DECLARATIVE_DROPDOWN_FORM_TYPE_LIMIT;
    }

    public static shouldLoadLegacyCultivoCatalogs(env: ParcelasEnvironmentDefinition): boolean {
        return env.showCultivosVariedadesPropios === true && (env.hasPlantas || !env.activateApiSIEX);
    }

    private static createSupportDataSource(
        key: ParcelasFormSupportDataKey,
        phase: ReactiveDropdownCatalogPhase,
    ): ReactiveFormSupportDataSource<ParcelasModel> {
        return {
            key,
            phase,
        };
    }
}
